// ---------------------------------------------------------------------------
// Guidebook — Public (unauthenticated) API client
// ---------------------------------------------------------------------------

import type { PrivateGuidebookPayload, PublicGuidebookPayload } from "./types"

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "http://127.0.0.1:8787"

export interface GuestAuthResult {
  ok: boolean
  guest_token: string
  expires_at?: string
  next?: string
  property?: {
    slug?: string
    name?: string
  }
}

export interface GuestAuthConfig {
  ok: boolean
  property?: { slug: string; name: string }
  mode?: "pms" | "ical"
  requires?: { last_name: boolean; check_in: boolean; check_out: boolean }
  pin?: { configured: boolean }
  error?: string
}

export function getGuestSessionStorageKey(slug: string): string {
  const normalized = String(slug || "").trim().toLowerCase()
  return `sf_guest_session:${normalized}`
}

export async function fetchGuestAuthConfig(slug: string): Promise<GuestAuthConfig> {
  const requestId = `gc-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`
  const res = await fetch(`${API_BASE_URL}/api/guest/auth-config/${encodeURIComponent(slug)}`, {
    headers: { "X-Request-Id": requestId },
  })
  const data = await res.json().catch(() => ({ ok: false, error: `HTTP ${res.status}` }))
  if (!res.ok || !data?.ok) {
    throw new Error(data?.error || `Failed to load login config (${res.status})`)
  }
  return data as GuestAuthConfig
}

export async function fetchGuidebook(key: string): Promise<PrivateGuidebookPayload> {
  const requestId = `gb-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`
  const res = await fetch(`${API_BASE_URL}/api/guidebook/${encodeURIComponent(key)}`, {
    headers: { "X-Request-Id": requestId },
  })
  if (!res.ok) {
    const data = await res.json().catch(() => ({ ok: false, error: `HTTP ${res.status}` }))
    throw new Error(data.error || `Failed to load guidebook (${res.status})`)
  }
  return res.json()
}

export async function authenticateGuestByBooking(
  slug: string,
  lastName: string,
  checkIn: string,
  checkOut?: string,
): Promise<GuestAuthResult> {
  const requestId = `ga-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`
  const res = await fetch(`${API_BASE_URL}/api/guest/auth`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Request-Id": requestId,
    },
    body: JSON.stringify({
      slug,
      last_name: lastName,
      check_in: checkIn,
      check_out: checkOut,
    }),
  })
  const data = await res.json().catch(() => ({ ok: false, error: `HTTP ${res.status}` }))
  if (!res.ok || !data?.ok || !data?.guest_token) {
    throw new Error(data?.error || `Failed to verify booking details (${res.status})`)
  }
  return data as GuestAuthResult
}

export async function unlockGuestGuidebook(slug: string, guestToken: string, pin: string): Promise<{ ok: boolean; unlocked?: boolean }> {
  const requestId = `gu-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`
  const res = await fetch(`${API_BASE_URL}/api/guest/unlock`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Request-Id": requestId,
      Authorization: `Guest ${guestToken}`,
    },
    body: JSON.stringify({ slug, pin }),
  })
  const data = await res.json().catch(() => ({ ok: false, error: `HTTP ${res.status}` }))
  if (!res.ok || !data?.ok) {
    throw new Error(data?.error || `Failed to unlock (${res.status})`)
  }
  return data as { ok: boolean; unlocked?: boolean }
}

export async function fetchPublicGuidebookBySlug(slug: string): Promise<PublicGuidebookPayload> {
  const requestId = `gp-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`
  const res = await fetch(`${API_BASE_URL}/api/public/guidebook/${encodeURIComponent(slug)}`, {
    headers: { "X-Request-Id": requestId },
  })
  if (!res.ok) {
    const data = await res.json().catch(() => ({ ok: false, error: `HTTP ${res.status}` }))
    throw new Error(data.error || `Failed to load guidebook (${res.status})`)
  }
  return res.json()
}

export async function fetchGuestGuidebookBySlug(slug: string, guestToken: string): Promise<PrivateGuidebookPayload> {
  const requestId = `gg-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`
  const res = await fetch(`${API_BASE_URL}/api/guest/guidebook/${encodeURIComponent(slug)}`, {
    headers: {
      "X-Request-Id": requestId,
      Authorization: `Guest ${guestToken}`,
    },
  })
  if (!res.ok) {
    const data = await res.json().catch(() => ({ ok: false, error: `HTTP ${res.status}` }))
    throw new Error(data.error || `Failed to load guidebook (${res.status})`)
  }
  return res.json()
}
